/**
 * cart-journey.js
 *
 * Fires when the user clicks Add to Cart on a product page and POSTs a
 * lightweight payload to webhooks.codeblue.agency/api/cart/custom.
 *
 * The server updates the lead's Journey → "Added to Cart" in the main
 * airtable (with the Pakistan / location-mismatch / journey transition
 * guards applied server-side).
 *
 * Reads product data from the product-form textareas already emitted by
 * snippets/product-form.liquid and snippets/product-form-new-design.liquid:
 *   [data-variant-json]         — full product.variants JSON
 *   [data-current-variant-json] — current variant JSON (multi-option products)
 *   input.selectedVaId          — currently selected variant id
 *
 * Fires as fire-and-forget — never blocks the native cart flow.
 *
 * ATC button selectors are centralized in ATC_SELECTOR below — if a new
 * product-form template ships a different class, edit that constant here
 * rather than the template.
 */
(function () {
  var CUSTOM_CART_URL = 'https://webhooks.codeblue.agency/api/cart/custom';
  var PROJECT_NAME = 'signmakerz.com';
  var PAYMENT_LINK_HOST = 'signmakerz.com';
  var GEO_API_URL = 'https://ipwho.is/';
  var GEO_WAIT_MS = 800;

  // Every ATC button variant across product-form snippets. Add new
  // selectors here rather than editing the templates.
  var ATC_SELECTOR = [
    'button[name="add"]',
    'button.add-to-cart',
    'button.sh-atc-btn',
    'button[data-add-to-cart]'
  ].join(', ');

  var geoPromise = null;


  /**
   * Walk every client-side source and collect {city, region, country, ip}.
   * The server requires ≥2 matching signals among {country, city/state, IP}
   * — this returns the raw signals so callers can decide whether to fetch
   * from an external API to fill gaps.
   */
  function collectLocationSignals() {
    var signals = { city: '', region: '', country: '', ip: '' };

    function fill(v, key) {
      if (!signals[key] && v && String(v).trim() !== '') {
        signals[key] = String(v).trim();
      }
    }

    function readLS(key) {
      try { return localStorage.getItem(key); } catch (e) { return null; }
    }
    function readSS(key) {
      try { return sessionStorage.getItem(key); } catch (e) { return null; }
    }

    // 1. Richest source: user_location_info JSON (written by fetchGeoFallback below).
    try {
      var raw = readLS('user_location_info');
      if (raw) {
        var info = JSON.parse(raw) || {};
        fill(info.city, 'city');
        fill(info.region || info.regionName || info.state, 'region');
        fill(info.country || info.countryName, 'country');
        fill(info.ip || info.query, 'ip');
      }
    } catch (e) { /* ignore parse errors */ }

    // 2. quicksignage-native format written by theme.liquid's fetchIPDetails():
    //    "city, region, country_name, timezone, ipAddress"
    //    (see layout/theme.liquid — localStorage keys `location` and `locationxh`.)
    var stringSources = [
      readLS('location'),
      readLS('locationxh'),
      readLS('ev_location_and_time'),
      readSS('ev_location_and_time')
    ];

    stringSources.forEach(function (str) {
      if (!str) return;
      str = String(str).trim();
      if (!str) return;

      var ipMatch = str.match(/\b\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\b/);
      if (ipMatch) fill(ipMatch[0], 'ip');

      var clean = str
        .replace(/\b\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\b/g, '')
        .replace(/\d{4}-\d{2}-\d{2}[t\s\d:.\-z+]*/gi, '');

      var parts = clean.split(/[,;]+/)
        .map(function (p) { return p.trim(); })
        .filter(function (p) {
          if (!p) return false;
          var low = p.toLowerCase();
          return low !== 'undefined' && low !== 'unknown' && low !== 'unknown country' && low !== 'n/a';
        });

      // quicksignage's `location` / `locationxh` are always
      // [city, region, country, timezone] after IP is stripped — the first
      // three slots are the ones we want, and any trailing timezone
      // ("America/New_York") is discarded.
      if (parts.length >= 3) {
        fill(parts[0], 'city');
        fill(parts[1], 'region');
        fill(parts[2], 'country');
      } else if (parts.length === 2) {
        fill(parts[0], 'region');
        fill(parts[1], 'country');
      } else if (parts.length === 1) {
        fill(parts[0], 'country');
      }
    });

    // 3. Country-only fallbacks.
    fill(readLS('countryName'), 'country');
    fill(readLS('shopCountryCode'), 'country');
    try {
      if (window.Shopify && window.Shopify.country) fill(window.Shopify.country, 'country');
    } catch (e) { /* ignore */ }

    // 4. Timezone hint — occasionally useful when nothing else survived.
    try {
      var tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz && !signals.city) {
        var seg = tz.split('/').pop();
        if (seg) fill(seg.replace(/_/g, ' '), 'city');
      }
    } catch (e) { /* ignore */ }

    return signals;
  }

  function signalsAreEnough(sig) {
    // Server needs country + at least one of {city, region, ip}.
    var hasCountry = !!(sig.country && String(sig.country).trim() !== '');
    var hasOther   = !!(
      (sig.city   && String(sig.city).trim()   !== '') ||
      (sig.region && String(sig.region).trim() !== '') ||
      (sig.ip     && String(sig.ip).trim()     !== '')
    );
    return hasCountry && hasOther;
  }

  function signalsToString(sig) {
    return [sig.city, sig.region, sig.country, sig.ip]
      .filter(function (p) { return p && String(p).trim() !== ''; })
      .join(', ');
  }

  /**
   * On-demand geo fetch — called only when local sources didn't yield
   * enough to satisfy the server's ≥2-signal rule. Result is merged into
   * `user_location_info` so the next collectLocationSignals() picks it up.
   */
  function fetchGeoFallback() {
    if (geoPromise) return geoPromise;

    geoPromise = fetch(GEO_API_URL, { method: 'GET' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        if (!d || d.success === false) return null;

        var incoming = {
          city:        d.city || '',
          region:      d.region || '',
          country:     d.country_code || d.country || '',
          countryName: d.country || '',
          ip:          d.ip || ''
        };

        try {
          var raw = localStorage.getItem('user_location_info');
          var existing = raw ? (JSON.parse(raw) || {}) : {};
          Object.keys(incoming).forEach(function (k) {
            if ((!existing[k] || String(existing[k]).trim() === '') && incoming[k]) {
              existing[k] = incoming[k];
            }
          });
          localStorage.setItem('user_location_info', JSON.stringify(existing));
          return existing;
        } catch (e) {
          return incoming;
        }
      })
      .catch(function () { return null; });

    return geoPromise;
  }

  /**
   * Read the product's variants array from the [data-variant-json] textarea
   * emitted by product-form.liquid. Returns [] if not present or malformed.
   */
  function readVariantsFromDom() {
    var el = document.querySelector('[data-variant-json]');
    if (!el) return [];
    try {
      var parsed = JSON.parse(el.value || el.textContent || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function readCurrentVariantFromDom() {
    var el = document.querySelector('[data-current-variant-json]');
    if (!el) return null;
    try {
      return JSON.parse(el.value || el.textContent || 'null');
    } catch (e) {
      return null;
    }
  }

  function pickImageUrl(variant, variants) {
    if (variant && variant.featured_image && variant.featured_image.src) {
      return variant.featured_image.src;
    }
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      if (v && v.featured_image && v.featured_image.src) return v.featured_image.src;
    }
    return null;
  }

  function buildPayload(locationString) {
    var variants = readVariantsFromDom();
    var currentVariant = readCurrentVariantFromDom();

    var urlObj = new URL(window.location.href);
    var paymentLinkPath = urlObj.pathname.replace(/-\d+$/, '');
    var paymentLink = PAYMENT_LINK_HOST + paymentLinkPath;

    var selectedVaEl = document.querySelector('input.selectedVaId, .selectedVaId');
    var variantId = urlObj.searchParams.get('variant') ||
      (currentVariant && currentVariant.id) ||
      (selectedVaEl && selectedVaEl.value) ||
      (variants[0] && variants[0].id) ||
      null;

    var selectedVariant = currentVariant;
    if (!selectedVariant && variantId) {
      for (var i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === String(variantId)) {
          selectedVariant = variants[i];
          break;
        }
      }
    }
    if (!selectedVariant && variants.length) selectedVariant = variants[0];

    var productId = (selectedVariant && selectedVariant.product_id) ||
      (variants[0] && variants[0].product_id) ||
      null;

    return {
      project_name: PROJECT_NAME,
      payment_link: paymentLink,
      variant_id: variantId ? String(variantId) : null,
      product_id: productId ? String(productId) : null,
      image_url: pickImageUrl(selectedVariant, variants),
      price: selectedVariant && selectedVariant.price != null
        ? selectedVariant.price / 100
        : null,
      location: locationString
    };
  }

  function postPayload(locationString) {
    try {
      var payload = buildPayload(locationString);

      fetch(CUSTOM_CART_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload),
        keepalive: true
      })
        .then(function (r) { return r.json(); })
        .then(function (d) { console.log('[cart-journey] response', d); })
        .catch(function (e) { console.warn('[cart-journey] request failed', e); });
    } catch (e) {
      console.warn('[cart-journey] unexpected error', e);
    }
  }

  function sendCustomCartUpdate() {
    var localSignals = collectLocationSignals();

    if (signalsAreEnough(localSignals)) {
      postPayload(signalsToString(localSignals));
      return;
    }

    // Local sources fell short — try the geo API, but cap the wait so we
    // never block the native cart flow. Whichever resolves first wins.
    var waitCap = new Promise(function (resolve) { setTimeout(resolve, GEO_WAIT_MS); });

    Promise.race([fetchGeoFallback(), waitCap]).then(function () {
      var merged = collectLocationSignals();
      postPayload(signalsToString(merged));
    });
  }

  var lastFireAt = 0;

  function handleAtcEvent(e) {
    var btn = e.target && e.target.closest ? e.target.closest(ATC_SELECTOR) : null;
    if (!btn || btn.disabled) return;
    // Debounce: pointerdown + click fire on the same interaction; only send once.
    var now = Date.now();
    if (now - lastFireAt < 1500) return;
    lastFireAt = now;
    sendCustomCartUpdate();
  }

  function bindAtc() {
    if (document.__cartJourneyBound) return;
    document.__cartJourneyBound = true;
    // Capture phase so stopPropagation() downstream can't hide the click from us.
    document.addEventListener('pointerdown', handleAtcEvent, true);
    document.addEventListener('click', handleAtcEvent, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindAtc);
  } else {
    bindAtc();
  }
})();
